import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import BottomNav from '../BottomNav';
import TimeRangePicker from './TimeRangePicker';
import theme from '../theme';

const categories = ['Babysitting', 'Dog sitting', 'Giardinaggio', 'Lavaggio auto', 'Pulizie in casa', 'Ripetizioni / Aiuto compiti', 'Trasloco / Facchinaggio', 'Videomaker / Fotografia', 'Taglio capelli/Acconciature', 'Trattamenti estetici', 'Altro'];

const suggestedRates = {
  'Babysitting': { min: 10, max: 14, unit: 'ora' },
  'Dog sitting': { min: 10, max: 20, unit: 'ora' },
  'Giardinaggio': { min: 15, max: 25, unit: 'ora' },
  'Lavaggio auto': { min: 15, max: 30, unit: 'fisso' },
  'Pulizie in casa': { min: 12, max: 20, unit: 'ora' },
  'Ripetizioni / Aiuto compiti': { min: 12, max: 20, unit: 'ora' },
  'Trasloco / Facchinaggio': { min: 20, max: 35, unit: 'ora' },
  'Videomaker / Fotografia': { min: 30, max: 80, unit: 'ora' },
  'Taglio capelli/Acconciature': { min: 10, max: 30, unit: 'fisso' },
  'Altro': { min: 10, max: 20, unit: 'ora' },
};

const legacyTimeSlotDefaults = {
  mattina: ['08:00', '13:00'],
  pomeriggio: ['13:00', '18:00'],
  sera: ['18:00', '22:00'],
  tutto_il_giorno: ['08:00', '18:00'],
};

async function getCoordsFromAddress(address) {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(address)}&format=json&limit=1`,
      { headers: { 'Accept-Language': 'it' } }
    );
    const data = await res.json();
    if (data && data.length > 0) {
      return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon), city: data[0].display_name.split(',')[0] };
    }
    return null;
  } catch { return null; }
}

function CalendarPicker({ selectedDates, onToggleDate }) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const [currentMonth, setCurrentMonth] = useState(new Date(today.getFullYear(), today.getMonth(), 1));

  const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay();
  const adjustedFirstDay = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;

  const monthNames = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
  const dayNames = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
  const dateKey = (year, month, day) => `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  const isPast = (day) => new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day) < today;

  return (
    <div style={{ background: theme.colors.white, borderRadius: theme.radius.lg, border: `1px solid ${theme.colors.border}`, overflow: 'hidden' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: theme.colors.primary, color: theme.colors.white }}>
        <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))} style={{ background: 'none', border: 'none', color: theme.colors.white, fontSize: 20, cursor: 'pointer' }}>‹</button>
        <span style={{ fontWeight: 700, fontSize: theme.font.size.lg }}>{monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}</span>
        <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))} style={{ background: 'none', border: 'none', color: theme.colors.white, fontSize: 20, cursor: 'pointer' }}>›</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', padding: '8px 8px 0' }}>
        {dayNames.map(d => <div key={d} style={{ textAlign: 'center', fontSize: theme.font.size.xs, color: theme.colors.textMuted, fontWeight: 600, padding: '4px 0' }}>{d}</div>)}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', padding: '4px 8px 12px', gap: 4 }}>
        {Array.from({ length: adjustedFirstDay }).map((_, i) => <div key={`empty-${i}`} />)}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const key = dateKey(currentMonth.getFullYear(), currentMonth.getMonth(), day);
          const isSelected = selectedDates.includes(key);
          const past = isPast(day);
          return (
            <div key={day} onClick={() => !past && onToggleDate(key)} style={{
              textAlign: 'center', padding: '8px 4px', borderRadius: theme.radius.sm,
              fontSize: theme.font.size.base, fontWeight: isSelected ? 700 : 400,
              background: isSelected ? theme.colors.primary : 'transparent',
              color: past ? '#ccc' : isSelected ? theme.colors.white : theme.colors.textPrimary,
              cursor: past ? 'not-allowed' : 'pointer', transition: 'all 0.15s'
            }}>{day}</div>
          );
        })}
      </div>
      {selectedDates.length > 0 && (
        <div style={{ padding: '8px 16px 12px', borderTop: '1px solid #f0f0f0', fontSize: theme.font.size.sm, color: theme.colors.primary }}>
          ✅ {selectedDates.length} {selectedDates.length === 1 ? 'giorno selezionato' : 'giorni selezionati'}
        </div>
      )}
    </div>
  );
}

function EditJob({ session }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: '', category: '', amount: '', priceType: 'ora',
    description: '', duration: '', people_needed: 1, requirements: '',
  });
  const [selectedDates, setSelectedDates] = useState([]);
  const [timeSlotMode, setTimeSlotMode] = useState('');
  const [fromTime, setFromTime] = useState('09:00');
  const [toTime, setToTime] = useState('13:00');
  const [position, setPosition] = useState(null);
  const [city, setCity] = useState('');
  const [manualAddress, setManualAddress] = useState('');
  const [searchingAddress, setSearchingAddress] = useState(false);
  const [addressFound, setAddressFound] = useState(false);
  const [existingPhotos, setExistingPhotos] = useState([]);
  const [newImageFiles, setNewImageFiles] = useState([]);
  const [newImagePreviews, setNewImagePreviews] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);

  useEffect(() => { fetchJob(); }, []);

  const fetchJob = async () => {
    const { data, error } = await supabase.from('jobs').select('*').eq('id', id).single();
    if (!error && data) {
      let amount = '';
      let priceType = 'ora';
      if (data.price) {
        const match = data.price.match(/€(\d+(?:\.\d+)?)/);
        if (match) amount = match[1];
        if (data.price.includes('fisso')) priceType = 'fisso';
      }
      setForm({
        title: data.title || '',
        category: data.category || '',
        amount,
        priceType,
        description: data.description || '',
        duration: data.duration || '',
        people_needed: data.people_needed || 1,
        requirements: data.requirements || '',
      });
      if (data.lat && data.lng) setPosition({ lat: data.lat, lng: data.lng });
      if (data.city) setCity(data.city);
      if (data.available_dates) setSelectedDates(data.available_dates);

      const ts = data.time_slot;
      if (ts) {
        const preciseMatch = ts.match(/^(\d{2}:\d{2})-(\d{2}:\d{2})$/);
        if (preciseMatch) {
          setTimeSlotMode('preciso');
          setFromTime(preciseMatch[1]);
          setToTime(preciseMatch[2]);
        } else if (ts === 'da_concordare') {
          setTimeSlotMode('da_concordare');
        } else if (legacyTimeSlotDefaults[ts]) {
          setTimeSlotMode('preciso');
          setFromTime(legacyTimeSlotDefaults[ts][0]);
          setToTime(legacyTimeSlotDefaults[ts][1]);
        }
      }

      const photos = data.image_urls || (data.image_url ? [data.image_url] : []);
      setExistingPhotos(photos);
    }
    setLoading(false);
  };

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleToggleDate = (dateKey) => {
    setSelectedDates(prev =>
      prev.includes(dateKey) ? prev.filter(d => d !== dateKey) : [...prev, dateKey].sort()
    );
  };

  const handleSearchAddress = async () => {
    if (!manualAddress.trim()) return;
    setSearchingAddress(true);
    setAddressFound(false);
    const result = await getCoordsFromAddress(manualAddress);
    if (result) {
      setPosition({ lat: result.lat, lng: result.lng });
      setCity(result.city);
      setAddressFound(true);
    }
    setSearchingAddress(false);
  };

  const handleNewImagesChange = (e) => {
    const files = Array.from(e.target.files);
    const remaining = 10 - existingPhotos.length - newImageFiles.length;
    const toAdd = files.slice(0, remaining);
    const oversized = toAdd.filter(f => f.size > 5 * 1024 * 1024);
    if (oversized.length > 0) { alert('Alcune foto superano i 5MB.'); return; }
    setNewImageFiles(prev => [...prev, ...toAdd]);
    setNewImagePreviews(prev => [...prev, ...toAdd.map(f => URL.createObjectURL(f))]);
  };

  const handleRemoveExisting = (index) => setExistingPhotos(prev => prev.filter((_, i) => i !== index));
  const handleRemoveNew = (index) => {
    setNewImageFiles(prev => prev.filter((_, i) => i !== index));
    setNewImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const priceLabel = form.amount ? `€${form.amount}${form.priceType === 'ora' ? '/h' : ' fisso'}` : '';
  const suggested = form.category ? suggestedRates[form.category] : null;
  const totalPhotos = existingPhotos.length + newImageFiles.length;
  const computedTimeSlot = timeSlotMode === 'preciso' ? `${fromTime}-${toTime}` : timeSlotMode === 'da_concordare' ? 'da_concordare' : '';

  const handleSave = async () => {
    if (!form.title || !form.category || !form.amount) {
      alert('Compila tutti i campi obbligatori!');
      return;
    }
    setSaving(true);

    let newUrls = [];
    if (newImageFiles.length > 0) {
      setUploadingImages(true);
      for (const file of newImageFiles) {
        const ext = file.name.split('.').pop();
        const fileName = `${session.user.id}_${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
        const { error: uploadError } = await supabase.storage.from('job-images').upload(fileName, file, { upsert: true });
        if (!uploadError) {
          const { data: urlData } = supabase.storage.from('job-images').getPublicUrl(fileName);
          newUrls.push(urlData.publicUrl);
        }
      }
      setUploadingImages(false);
    }

    const allPhotos = [...existingPhotos, ...newUrls];

    const { error } = await supabase.from('jobs').update({
      title: form.title,
      category: form.category,
      price: priceLabel,
      description: form.description,
      lat: position?.lat || null,
      lng: position?.lng || null,
      city: city || null,
      duration: form.duration.trim() || null,
      people_needed: parseInt(form.people_needed) || 1,
      requirements: form.requirements.trim() || null,
      image_url: allPhotos[0] || null,
      image_urls: allPhotos.length > 0 ? allPhotos : null,
      available_dates: selectedDates.length > 0 ? selectedDates : null,
      time_slot: computedTimeSlot || null,
    }).eq('id', id);

    setSaving(false);
    if (error) { alert('Errore nel salvataggio: ' + error.message); }
    else { navigate('/profilo'); }
  };

  if (loading) return <div style={{ padding: theme.spacing.xl, textAlign: 'center', color: theme.colors.textMuted }}>Caricamento...</div>;

  return (
    <div style={{ maxWidth: theme.layout.maxWidth, margin: '0 auto', fontFamily: theme.font.family }}>
      <div style={{ background: theme.colors.primary, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: theme.spacing.md }}>
        <button onClick={() => navigate('/profilo')} style={{ background: 'none', border: 'none', color: theme.colors.white, fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
        <h2 style={{ margin: 0, color: theme.colors.white, fontSize: theme.font.size.xxl }}>Modifica annuncio</h2>
      </div>

      <div style={{ padding: theme.spacing.xl, paddingBottom: 80 }}>

        <div style={{ border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.md, padding: 14, marginBottom: theme.spacing.xl }}>
          <div style={{ fontSize: theme.font.size.base, fontWeight: 600, color: '#444', marginBottom: theme.spacing.sm }}>📍 Posizione dell'annuncio</div>
          {city && (
            <div style={{ background: '#f0f9f0', borderRadius: theme.radius.sm, padding: 8, marginBottom: theme.spacing.sm, fontSize: theme.font.size.base, color: theme.colors.primary }}>
              ✅ Posizione attuale: <strong>{city}</strong>
            </div>
          )}
          <div style={{ fontSize: theme.font.size.sm, color: theme.colors.textMuted, marginBottom: theme.spacing.sm }}>Modifica la posizione:</div>
          <div style={{ display: 'flex', gap: theme.spacing.sm }}>
            <input value={manualAddress} onChange={e => setManualAddress(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearchAddress()}
              placeholder="Es. Via Roma 1, Parma"
              style={{ flex: 1, padding: '8px 12px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borderInput}`, fontSize: theme.font.size.base, boxSizing: 'border-box' }} />
            <button onClick={handleSearchAddress} disabled={searchingAddress} style={{
              background: searchingAddress ? theme.colors.textMuted : theme.colors.primary, color: theme.colors.white,
              border: 'none', borderRadius: theme.radius.sm, padding: '8px 14px',
              fontSize: theme.font.size.base, cursor: searchingAddress ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap'
            }}>{searchingAddress ? '...' : '🔍 Cerca'}</button>
          </div>
          {addressFound && <div style={{ fontSize: theme.font.size.sm, color: theme.colors.primary, marginTop: 6 }}>✅ Nuova posizione: <strong>{city}</strong></div>}
        </div>

        <div style={{ marginBottom: theme.spacing.lg }}>
          <label style={{ fontSize: theme.font.size.base, color: theme.colors.textSecondary, display: 'block', marginBottom: 6 }}>Titolo *</label>
          <input name="title" value={form.title} onChange={handleChange} placeholder="Es. Ripetizioni di inglese"
            style={{ width: '100%', padding: '10px 12px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borderInput}`, fontSize: theme.font.size.md, boxSizing: 'border-box' }} />
        </div>

        <div style={{ marginBottom: theme.spacing.lg }}>
          <label style={{ fontSize: theme.font.size.base, color: theme.colors.textSecondary, display: 'block', marginBottom: 6 }}>Categoria *</label>
          <select name="category" value={form.category} onChange={handleChange}
            style={{ width: '100%', padding: '10px 12px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borderInput}`, fontSize: theme.font.size.md, boxSizing: 'border-box' }}>
            <option value="">Seleziona categoria</option>
            {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
          </select>
        </div>

        {suggested && (
          <div style={{ background: theme.colors.warningBg, borderRadius: theme.radius.sm, padding: '8px 12px', marginBottom: theme.spacing.md, fontSize: theme.font.size.sm, color: theme.colors.warning }}>
            💡 Tariffa suggerita: <strong>€{suggested.min}–€{suggested.max}/{suggested.unit}</strong>
          </div>
        )}

        <div style={{ marginBottom: theme.spacing.lg }}>
          <label style={{ fontSize: theme.font.size.base, color: theme.colors.textSecondary, display: 'block', marginBottom: 6 }}>Compenso *</label>
          <div style={{ display: 'flex', gap: theme.spacing.sm, alignItems: 'center' }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: theme.colors.primary, fontWeight: 700, fontSize: theme.font.size.xl }}>€</span>
              <input name="amount" type="number" value={form.amount} onChange={handleChange} placeholder="0" min="0"
                style={{ width: '100%', padding: '10px 12px 10px 28px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borderInput}`, fontSize: theme.font.size.md, boxSizing: 'border-box' }} />
            </div>
            <select name="priceType" value={form.priceType} onChange={handleChange}
              style={{ padding: '10px 12px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borderInput}`, fontSize: theme.font.size.md, background: theme.colors.white }}>
              <option value="ora">/ ora</option>
              <option value="fisso">fisso</option>
            </select>
          </div>
          {priceLabel && <div style={{ marginTop: 6, fontSize: theme.font.size.base, color: theme.colors.primary }}>Verrà mostrato come: <strong>{priceLabel}</strong></div>}
        </div>

        <div style={{ marginBottom: theme.spacing.lg }}>
          <label style={{ fontSize: theme.font.size.base, color: theme.colors.textSecondary, display: 'block', marginBottom: 6 }}>📅 Giorni disponibili</label>
          <CalendarPicker selectedDates={selectedDates} onToggleDate={handleToggleDate} />
        </div>

        <div style={{ marginBottom: theme.spacing.lg }}>
          <label style={{ fontSize: theme.font.size.base, color: theme.colors.textSecondary, display: 'block', marginBottom: theme.spacing.sm }}>🕐 Fascia oraria</label>
          <div style={{ display: 'flex', gap: theme.spacing.sm, marginBottom: theme.spacing.sm }}>
            <button onClick={() => setTimeSlotMode('preciso')} style={{
              flex: 1, padding: '10px', borderRadius: theme.radius.md, cursor: 'pointer',
              border: timeSlotMode === 'preciso' ? `2px solid ${theme.colors.primary}` : `1px solid ${theme.colors.border}`,
              background: timeSlotMode === 'preciso' ? theme.colors.primaryLight : theme.colors.white,
              fontWeight: timeSlotMode === 'preciso' ? 700 : 400, fontSize: theme.font.size.base,
            }}>🕐 Orario preciso</button>
            <button onClick={() => setTimeSlotMode('da_concordare')} style={{
              flex: 1, padding: '10px', borderRadius: theme.radius.md, cursor: 'pointer',
              border: timeSlotMode === 'da_concordare' ? `2px solid ${theme.colors.primary}` : `1px solid ${theme.colors.border}`,
              background: timeSlotMode === 'da_concordare' ? theme.colors.primaryLight : theme.colors.white,
              fontWeight: timeSlotMode === 'da_concordare' ? 700 : 400, fontSize: theme.font.size.base,
            }}>💬 Da concordare</button>
          </div>

          {timeSlotMode === 'preciso' && (
            <div style={{ border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.lg, padding: theme.spacing.lg, background: theme.colors.white }}>
              <TimeRangePicker fromTime={fromTime} toTime={toTime} onChangeFrom={setFromTime} onChangeTo={setToTime} />
              <div style={{ textAlign: 'center', marginTop: 8, fontSize: theme.font.size.base, color: theme.colors.primary, fontWeight: 600 }}>
                🕐 Dalle {fromTime} alle {toTime}
              </div>
            </div>
          )}

          {timeSlotMode === 'da_concordare' && (
            <div style={{ background: theme.colors.primaryLight, borderRadius: theme.radius.md, padding: '10px 14px', fontSize: theme.font.size.base, color: theme.colors.primary, textAlign: 'center' }}>
              💬 L'orario sarà da concordare direttamente con il lavoratore
            </div>
          )}
        </div>

        <div style={{ marginBottom: theme.spacing.lg }}>
          <label style={{ fontSize: theme.font.size.base, color: theme.colors.textSecondary, display: 'block', marginBottom: 6 }}>⏱️ Durata stimata</label>
          <input name="duration" value={form.duration} onChange={handleChange}
            placeholder="Es. 2 ore, mezza giornata..."
            style={{ width: '100%', padding: '10px 12px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borderInput}`, fontSize: theme.font.size.md, boxSizing: 'border-box' }} />
        </div>

        <div style={{ marginBottom: theme.spacing.lg }}>
          <label style={{ fontSize: theme.font.size.base, color: theme.colors.textSecondary, display: 'block', marginBottom: 6 }}>👥 Numero di persone cercate</label>
          <select name="people_needed" value={form.people_needed} onChange={handleChange}
            style={{ width: '100%', padding: '10px 12px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borderInput}`, fontSize: theme.font.size.md, boxSizing: 'border-box' }}>
            {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n} {n === 1 ? 'persona' : 'persone'}</option>)}
          </select>
        </div>

        <div style={{ marginBottom: theme.spacing.lg }}>
          <label style={{ fontSize: theme.font.size.base, color: theme.colors.textSecondary, display: 'block', marginBottom: 6 }}>📋 Richieste</label>
          <textarea name="requirements" value={form.requirements} onChange={handleChange}
            placeholder="Es. Esperienza con bambini, patente B..." rows={3}
            style={{ width: '100%', padding: '10px 12px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borderInput}`, fontSize: theme.font.size.md, boxSizing: 'border-box', resize: 'none' }} />
        </div>

        <div style={{ marginBottom: theme.spacing.lg }}>
          <label style={{ fontSize: theme.font.size.base, color: theme.colors.textSecondary, display: 'block', marginBottom: 6 }}>📝 Descrizione</label>
          <textarea name="description" value={form.description} onChange={handleChange}
            placeholder="Descrivi il lavoro nel dettaglio..." rows={4}
            style={{ width: '100%', padding: '10px 12px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borderInput}`, fontSize: theme.font.size.md, boxSizing: 'border-box', resize: 'none' }} />
        </div>

        <div style={{ marginBottom: theme.spacing.xxl }}>
          <label style={{ fontSize: theme.font.size.base, color: theme.colors.textSecondary, display: 'block', marginBottom: 6 }}>
            🖼️ Foto dell'annuncio <span style={{ color: theme.colors.textMuted }}>({totalPhotos}/10)</span>
          </label>
          {existingPhotos.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: theme.spacing.sm, marginBottom: 8 }}>
              {existingPhotos.map((url, index) => (
                <div key={index} style={{ position: 'relative', borderRadius: theme.radius.sm, overflow: 'hidden', aspectRatio: '1' }}>
                  <img src={url} alt={`foto ${index + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  <button onClick={() => handleRemoveExisting(index)} style={{
                    position: 'absolute', top: 4, right: 4, background: 'rgba(0,0,0,0.6)', color: theme.colors.white,
                    border: 'none', borderRadius: theme.radius.circle, width: 22, height: 22,
                    fontSize: theme.font.size.sm, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>✕</button>
                </div>
              ))}
            </div>
          )}
          {newImagePreviews.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: theme.spacing.sm, marginBottom: 8 }}>
              {newImagePreviews.map((preview, index) => (
                <div key={index} style={{ position: 'relative', borderRadius: theme.radius.sm, overflow: 'hidden', aspectRatio: '1' }}>
                  <img src={preview} alt={`nuova foto ${index + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  <button onClick={() => handleRemoveNew(index)} style={{
                    position: 'absolute', top: 4, right: 4, background: 'rgba(0,0,0,0.6)', color: theme.colors.white,
                    border: 'none', borderRadius: theme.radius.circle, width: 22, height: 22,
                    fontSize: theme.font.size.sm, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>✕</button>
                </div>
              ))}
            </div>
          )}
          {totalPhotos < 10 && (
            <div onClick={() => document.getElementById('edit-images-input').click()}
              style={{ border: `2px dashed ${theme.colors.borderInput}`, borderRadius: theme.radius.md, padding: theme.spacing.lg, textAlign: 'center', cursor: 'pointer', background: theme.colors.background }}>
              <div style={{ fontSize: 28, marginBottom: 6 }}>📷</div>
              <div style={{ fontSize: theme.font.size.base, color: theme.colors.textMuted }}>{totalPhotos === 0 ? 'Clicca per aggiungere foto' : 'Aggiungi altre foto'}</div>
              <div style={{ fontSize: theme.font.size.xs, color: '#bbb', marginTop: 2 }}>Max 10 foto, 5MB ciascuna</div>
            </div>
          )}
          <input id="edit-images-input" type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleNewImagesChange} />
        </div>

        <button onClick={handleSave} disabled={saving} style={{
          width: '100%', background: saving ? theme.colors.textMuted : theme.colors.primary, color: theme.colors.white,
          border: 'none', borderRadius: theme.radius.lg, padding: 14,
          fontSize: theme.font.size.xl, cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 600
        }}>{saving ? (uploadingImages ? 'Caricamento foto...' : 'Salvataggio...') : '✓ Salva modifiche'}</button>
      </div>

      <BottomNav />
    </div>
  );
}

export default EditJob;